/** Default Google Analytics measurement ID */
const DEFAULT_GA_MEASUREMENT_ID = "G-R5C2L9ZSYS";
/** Active measurement ID for the current app runtime */
let activeMeasurementId = DEFAULT_GA_MEASUREMENT_ID;
/**
 * Returns true when running in a production environment.
 * Uses hostname detection so it works across all bundlers
 * (Vite, CRA, Next.js) without relying on build-tool-specific env vars.
 */
function isProduction() {
    if (typeof window === "undefined")
        return false;
    const host = window.location.hostname;
    return (host !== "localhost" && host !== "127.0.0.1" && !host.startsWith("192.168."));
}
/**
 * Initialize Google Analytics.
 * Only loads in production to avoid polluting development data.
 * Uses async script loading for optimal performance.
 */
export function initGA(measurementId = DEFAULT_GA_MEASUREMENT_ID) {
    if (!isProduction())
        return;
    if (document.querySelector('script[src*="googletagmanager"]'))
        return;
    activeMeasurementId = measurementId;
    const script = document.createElement("script");
    script.src = `https://www.googletagmanager.com/gtag/js?id=${activeMeasurementId}`;
    script.async = true;
    document.head.appendChild(script);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer.push(arguments);
    };
    window.gtag("js", new Date());
    window.gtag("config", activeMeasurementId, {
        send_page_view: true,
    });
}
/**
 * Track a page view event.
 * @param path - The page path (defaults to current pathname)
 * @param title - The page title (defaults to document.title)
 */
export function trackPageView(path, title) {
    if (!isProduction() || !window.gtag)
        return;
    window.gtag("event", "page_view", {
        page_path: path ?? window.location.pathname,
        page_title: title ?? document.title,
    });
}
/**
 * Track a button click or UI interaction.
 * @param label - A descriptive label for the click target
 * @param category - Event category (defaults to 'engagement')
 */
export function trackClick(label, category = "engagement") {
    if (!isProduction() || !window.gtag)
        return;
    window.gtag("event", "click", {
        event_category: category,
        event_label: label,
    });
}
/**
 * Track a custom analytics event.
 * @param action - The event action name
 * @param params - Additional event parameters
 */
export function trackEvent(action, params) {
    if (!isProduction() || !window.gtag)
        return;
    window.gtag("event", action, params);
}
